import { recordCalibrationEvent } from '@framework/utils/operationCalibration';
import { getClientId } from '@framework/app';
import { useHandler } from '@framework/hooks';
import { useCommandManager } from '@model-framework/command';
import { HyperLinkShow, HyperLinkViewModelIcon } from '@model-framework/hyperlink';
import type { RectShape } from '@model-framework/shape';
import type { FontSize } from '@model-framework/text';
import { EditingUser, EditingUserIconView, useOtherUserEditingToast } from '@model-framework/text/editing-user';
import type { ModelId, NodeId, ViewModelId } from 'balus-common';
import type { UserPublicProfile } from '@user/PublicProfile';
import type { NodeKey } from '@view-model/domain/key';
import { Point } from '@view-model/models/common/basic';
import type { ThemeColor } from '@view-model/models/common/color';
import type { TextAlign } from '@view-model/models/common/text-align';
import { LinkCreator } from '@view-model/models/common/components/LinkCreator';
import type { Position } from '@view-model/models/common/types/ui';
import type { DragContext } from '@view-model/models/framework/ui';
import { CreatedUserView, DraggableSVGGElement } from '@view-model/models/framework/ui';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NodeOperation } from './adapter';
import { LinkableTargetContainer, NodeMenu, NodeOtherUserSelected, NodeSelected, NodeTextarea } from './components';
import type { NodeName, NodeStyle, StickyNode } from './domain';
import { NodeFontSize } from './domain';
import { NodeReactionStatusView } from '../NodeReaction/components';
import { useNodeReaction } from '../NodeReaction';
import { useNodeEditingUser } from '../editing-users/useNodeEditingUser';
import { useOtherUserIsSelected } from '../client-selected-items/useOtherUserIsSelected';
import { useRightClickMenuOpenState } from '@user/pages/ViewModelPage/useRightClickMenuOpenState';
import { RightClickMenu } from '@framework/ui/elements/RightClickMenu';
import {
  ColorMenuButton,
  HyperLinkMenuButton,
  TextSizeMenuButton,
  TextAlignMenuButton,
  ReactionMenuButton,
} from './components/NodeRightClickMenuItem';

// NOTE: Propsを変えたら、propsAreEqual()も変えること
type Props = {
  readonly: boolean;
  viewModelId: ViewModelId;
  modelId: ModelId;
  node: StickyNode;
  position: Point;
  currentUserProfile: UserPublicProfile;
  isSelected: boolean;
  isMultiSelected: boolean;
  isLinkableTarget: boolean;
  nodeShape: RectShape;
  baseColor: string;
  showMenu: boolean;
  showNodeCreatedUser: boolean;
  startEditOnMount: boolean;
  onClick(id: NodeId, e: React.MouseEvent<SVGGElement>): void;
  onDragStart(id: NodeId, e: React.MouseEvent<SVGGElement>): void;
  onDrag(context: DragContext): void;
  onDragEnd(context: DragContext): void;
  onEditStart(id: NodeId): void;
  onNameEditStartedOnMount(): void;
  onLinkerStart(key: NodeKey, startPosition: Position): void;
  onLinkerMove(currentPosition: Position): void;
  onLinkerEnd(currentPosition: Position): void;
  onClickViewModelLink(e: React.MouseEvent): void;
  onGroupSelectedNode(): void;
  onDelete(): void;
  onFontSizeSelected(fontSize: FontSize): void;
  onToggleFontStrikethrough(fontStrikethrough: boolean): void;
  onThemeColorSelected(color: ThemeColor): void;
  onTextAlignSelected(textAlign: TextAlign): void;
};

type State = {
  name: NodeName;
  previousName: NodeName;
  editingName: boolean;
};

type RightClickMenuButtonRef = {
  closePopup: () => void;
};

const StickyNodeViewFn = (props: Props) => {
  const { t } = useTranslation();
  const commandManager = useCommandManager();
  const { activeRightClickMenuIds, setActiveRightClickMenuIds } = useRightClickMenuOpenState();
  const [state, setState] = useState<State>({
    name: props.node.name,
    previousName: props.node.name,
    editingName: false,
  });

  const user = useMemo(() => {
    return EditingUser.buildFromProfile(props.currentUserProfile, getClientId());
  }, [props.currentUserProfile]);

  const editingUser = useNodeEditingUser(props.node.key);

  const notifyOtherUserEditingToast = useOtherUserEditingToast();

  const otherUserEditing = !!(editingUser && !editingUser.isEqual(user));

  const otherUserSelected = useOtherUserIsSelected({
    nodeId: props.node.id,
    clientId: getClientId(),
    modelId: props.modelId,
  });

  const nodeOperation = useMemo(
    () => new NodeOperation(props.viewModelId, props.modelId, props.node.key, commandManager, props.currentUserProfile),
    [props.viewModelId, props.modelId, props.node.key, commandManager, props.currentUserProfile]
  );

  const isViewModelUpdatable = !props.readonly;

  const handleNameStartEdit = useHandler((): void => {
    if (!isViewModelUpdatable) return;
    props.onEditStart(props.node.id);

    if (otherUserEditing) {
      notifyOtherUserEditingToast();
      return;
    }

    setState((prev) => ({
      ...prev,
      name: props.node.name,
      previousName: props.node.name,
      editingName: true,
    }));

    recordCalibrationEvent('text.start', { node: 1 });
    nodeOperation.saveMyEditingUser();
  });

  useEffect(() => {
    if (props.startEditOnMount) {
      handleNameStartEdit();
      props.onNameEditStartedOnMount();
    }
  });

  const updateStyle = useHandler((style: NodeStyle): void => {
    const currentStyle = props.node.style;
    nodeOperation.handleUpdateStyle(style, currentStyle);
  });

  const handleChangeFontSize = useHandler((newFontSize: FontSize): void => {
    const newNodeFontSize = NodeFontSize.fromFontSize(newFontSize);
    updateStyle(props.node.style.withFontSize(newNodeFontSize));
  });

  const handleNameChange = useHandler((name: NodeName): Promise<void> => {
    setState((prev) => ({ ...prev, name }));
    return nodeOperation.handleEditingNameChanged(name) ?? Promise.resolve();
  });

  const handleNameEndEdit = useHandler((name: NodeName): void => {
    setState((prev) => ({ ...prev, editingName: false }));
    recordCalibrationEvent('text.end', { node: 1 });
    nodeOperation.clearMyEditingUser();
    nodeOperation.confirmEditName(name, state.previousName).then();
  });

  const handleNodeDragStart = useHandler((e: React.MouseEvent<SVGGElement>): void => {
    if (props.readonly) {
      return;
    }
    props.onDragStart(props.node.id, e);
  });

  const handleNodeDrag = useHandler((context: DragContext): void => {
    props.onDrag(context);
  });

  const handleNodeDragEnd = useHandler((context: DragContext): void => {
    props.onDragEnd(context);
  });

  const handleNodeClick = useHandler((e: React.MouseEvent<SVGGElement>): void => {
    props.onClick(props.node.id, e);
  });

  const handleNodeRightClick = useHandler((e: React.MouseEvent<SVGGElement>): void => {
    // 選択状態にしてから右クリックメニューを表示する
    props.onClick(props.node.id, e);
    setActiveRightClickMenuIds([props.node.id]);
    e.preventDefault();
  });

  const shouldShowCreatedUser = props.showNodeCreatedUser && props.node.createdUserKey !== null;

  const handleLinkerStart = useHandler((startPosition: Position): void => {
    const { x, y } = startPosition;
    props.onLinkerStart(props.node.key, props.node.position.add(x, y));
  });

  const handleLinkerMove = useHandler((currentPosition: Position): void => {
    const { x, y } = currentPosition;
    props.onLinkerMove(props.node.position.add(x, y));
  });

  const handleLinkerEnd = useHandler((currentPosition: Position): void => {
    const { x, y } = currentPosition;
    props.onLinkerEnd(props.node.position.add(x, y));
  });

  const handleToggleNodeDescription = useHandler((): void => {
    nodeOperation.toggleNodeDescription();
  });

  const handleToggleFontStrikethrough = useHandler(() => {
    props.onToggleFontStrikethrough(!props.node.style.fontStrikethrough);
  });

  const handleSaveHyperLink = useHandler((url: string | null): void => {
    nodeOperation.handleEditingURLChanged(url, props.node.url);
  });

  const { width, height } = props.node.size();
  const baseMargin = 8;
  const baseRectShape = props.nodeShape.resize({
    dWidth: baseMargin * 2,
    dHeight: baseMargin * 2,
  });

  const nodeMenuPosition = props.position.addXY(width / 2 + 32, 0);
  const nodeRightClickMenuPosition = props.position.addXY(width / 2, height / 2);

  const { fillColor, borderColor } = props.node.style.getStyles();

  const { reaction, toggleReaction } = useNodeReaction(props.viewModelId, props.modelId, props.node.id);

  const textSizeMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const colorMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const textAlignMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const hyperLinkMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);
  const reactionMenuButtonRef = useRef<RightClickMenuButtonRef | null>(null);

  const closeOtherRightClickSubMenu = useHandler((menuType: string): void => {
    if (menuType !== 'textSize') textSizeMenuButtonRef.current?.closePopup();
    if (menuType !== 'color') colorMenuButtonRef.current?.closePopup();
    if (menuType !== 'textAlign') textAlignMenuButtonRef.current?.closePopup();
    if (menuType !== 'hyperlink') hyperLinkMenuButtonRef.current?.closePopup();
    if (menuType !== 'reaction') reactionMenuButtonRef.current?.closePopup();
  });

  return (
    <g>
      <DraggableSVGGElement
        position={props.position}
        onDragMoveStarted={handleNodeDragStart}
        onDragMoveEnded={handleNodeDragEnd}
        onDrag={handleNodeDrag}
        onClick={handleNodeClick}
        onContextMenu={isViewModelUpdatable ? handleNodeRightClick : undefined}
      >
        {/* ゾーンに含まれているか否かを区別するための土台の色 (ゾーンに含まれない時にはキャンバスと同じ白色) */}
        <g transform={`translate(${-baseMargin},${-baseMargin})`}>
          {baseRectShape.render({ fill: props.baseColor, cursor: 'move' })}
        </g>

        <g>
          {props.nodeShape.render({
            fill: fillColor,
            stroke: borderColor,
            strokeWidth: 2,
          })}
        </g>
        {isViewModelUpdatable && props.showMenu && (
          <LinkCreator
            elementSize={{ width, height }}
            onLinkerStart={handleLinkerStart}
            onLinkerMove={handleLinkerMove}
            onLinkerEnd={handleLinkerEnd}
          />
        )}
        <NodeTextarea
          width={width}
          height={height}
          isMyEditing={state.editingName}
          otherUserEditing={otherUserEditing}
          name={state.editingName ? state.name : props.node.name}
          fontSize={props.node.style.fontSize}
          fontStrikethrough={props.node.style.fontStrikethrough}
          textAlign={props.node.style.textAlign}
          themeColor={props.node.style.themeColor}
          onStartEdit={handleNameStartEdit}
          onEndEdit={handleNameEndEdit}
          onChange={handleNameChange}
          onChangeFontSize={handleChangeFontSize}
          isHyperLink={!!props.node.url}
        />
        {
          // 自分が対象の付箋を選択している場合に枠線で囲む
          props.isSelected || props.isMultiSelected ? (
            <NodeSelected shape={props.nodeShape} margin={6} />
          ) : // 他のユーザが選択している場合にも枠線で囲む
          otherUserSelected ? (
            <NodeOtherUserSelected shape={props.nodeShape} margin={12} />
          ) : null
        }
        {editingUser && (
          <EditingUserIconView currentEditingUser={editingUser} position={new Point(0, height)} iconSize={48} />
        )}
        {shouldShowCreatedUser && props.node.createdUserId && !editingUser && (
          <CreatedUserView userId={props.node.createdUserId} position={new Point(0, height)} />
        )}

        {props.isLinkableTarget && <LinkableTargetContainer shape={props.nodeShape} margin={12} />}

        {/* 選択時のハイパーリンク表示 */}
        {props.isSelected && props.node.url && (
          <HyperLinkShow
            x={width + 25}
            y={height}
            url={props.node.url}
            viewModelId={props.viewModelId}
            onClickViewModelLink={props.onClickViewModelLink}
          />
        )}

        {/* 付箋左上のアイコン表示 */}
        {props.node.url && <HyperLinkViewModelIcon url={props.node.url} />}
      </DraggableSVGGElement>

      {isViewModelUpdatable && props.showMenu && (
        <NodeMenu
          currentStyle={props.node.style}
          offset={nodeMenuPosition}
          textEditing={state.editingName}
          url={props.node.url}
          onDeleteNode={props.onDelete}
          onGroupSelectedNode={props.onGroupSelectedNode}
          onToggleNodeDescription={handleToggleNodeDescription}
          onSaveHyperLink={handleSaveHyperLink}
          onFontSizeSelected={props.onFontSizeSelected}
          onToggleFontStrikethrough={handleToggleFontStrikethrough}
          onThemeColorSelected={props.onThemeColorSelected}
          onTextAlignSelected={props.onTextAlignSelected}
          reaction={reaction}
          toggleReaction={toggleReaction}
        />
      )}

      <NodeReactionStatusView
        reaction={reaction}
        toggleReaction={toggleReaction}
        nodeRect={props.node.getRect()}
        iconSize={40}
      />

      <RightClickMenu
        isOpen={activeRightClickMenuIds.length === 1 && activeRightClickMenuIds[0] === props.node.id}
        mousePosition={nodeRightClickMenuPosition}
      >
        <TextSizeMenuButton
          ref={textSizeMenuButtonRef}
          onChangeTextSize={props.onFontSizeSelected}
          onOpenSubMenu={closeOtherRightClickSubMenu}
        />
        <ColorMenuButton
          ref={colorMenuButtonRef}
          onChangeColor={props.onThemeColorSelected}
          onOpenSubMenu={closeOtherRightClickSubMenu}
        />
        <TextAlignMenuButton
          ref={textAlignMenuButtonRef}
          onChangeTextAlign={props.onTextAlignSelected}
          onOpenSubMenu={closeOtherRightClickSubMenu}
        />
        <div onClick={handleToggleFontStrikethrough} className="w-full cursor-pointer px-12 py-4 hover:bg-gray-200">
          {t('canvas.menu.strikethrough')}
        </div>
        <HyperLinkMenuButton
          ref={hyperLinkMenuButtonRef}
          onClose={() => {
            setActiveRightClickMenuIds([]);
          }}
          initialURL={props.node.url}
          onSave={(url: string | null) => {
            handleSaveHyperLink(url);
            setActiveRightClickMenuIds([]);
          }}
          onOpenSubMenu={closeOtherRightClickSubMenu}
        />
        <div onClick={handleToggleNodeDescription} className="w-full cursor-pointer px-12 py-4 hover:bg-gray-200">
          {t('canvas.menu.note')}
        </div>
        <div onClick={props.onGroupSelectedNode} className="w-full cursor-pointer px-12 py-4 hover:bg-gray-200">
          {t('canvas.menu.group')}
        </div>
        <ReactionMenuButton
          ref={reactionMenuButtonRef}
          onReactionSelected={toggleReaction}
          onOpenSubMenu={closeOtherRightClickSubMenu}
        />
        <div onClick={props.onDelete} className="w-full cursor-pointer px-12 py-4 hover:bg-gray-200">
          {t('common.action.delete')}
        </div>
      </RightClickMenu>
    </g>
  );
};

function propsAreEqual(prevProps: Props, nextProps: Props): boolean {
  return (
    prevProps.readonly === nextProps.readonly &&
    prevProps.viewModelId === nextProps.viewModelId &&
    prevProps.modelId === nextProps.modelId &&
    prevProps.node.isEqual(nextProps.node) &&
    prevProps.position.isEqual(nextProps.position) &&
    prevProps.currentUserProfile === nextProps.currentUserProfile &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.isMultiSelected === nextProps.isMultiSelected &&
    prevProps.isLinkableTarget === nextProps.isLinkableTarget &&
    prevProps.nodeShape.isEqual(nextProps.nodeShape) &&
    prevProps.baseColor === nextProps.baseColor &&
    prevProps.showMenu === nextProps.showMenu &&
    prevProps.showNodeCreatedUser === nextProps.showNodeCreatedUser &&
    prevProps.startEditOnMount === nextProps.startEditOnMount &&
    prevProps.onClick === nextProps.onClick &&
    prevProps.onDragStart === nextProps.onDragStart &&
    prevProps.onDrag === nextProps.onDrag &&
    prevProps.onDragEnd === nextProps.onDragEnd &&
    prevProps.onEditStart === nextProps.onEditStart &&
    prevProps.onNameEditStartedOnMount === nextProps.onNameEditStartedOnMount &&
    prevProps.onLinkerStart === nextProps.onLinkerStart &&
    prevProps.onLinkerMove === nextProps.onLinkerMove &&
    prevProps.onLinkerEnd === nextProps.onLinkerEnd &&
    prevProps.onClickViewModelLink === nextProps.onClickViewModelLink &&
    prevProps.onGroupSelectedNode === nextProps.onGroupSelectedNode &&
    prevProps.onDelete === nextProps.onDelete &&
    prevProps.onFontSizeSelected === nextProps.onFontSizeSelected &&
    prevProps.onToggleFontStrikethrough === nextProps.onToggleFontStrikethrough &&
    prevProps.onThemeColorSelected === nextProps.onThemeColorSelected
  );
}

export const StickyNodeView = memo(StickyNodeViewFn, propsAreEqual);
